/**
 * VatRegisterColumnsCanvas — podgląd klocka „Rejestr VAT" na płótnie kreatora formatek.
 *
 * Płótno pokazuje EDYTOR UKŁADU KOLUMN (`TableLayoutEditor`): nagłówki 15 kolumn rejestru z jednym
 * wierszem przykładowym, a przy możliwości zapisu — przeciąganie, szerokość, menu kolumny i panel
 * „Kolumny". Każda akcja wysyła jedną łatkę `settings` (`tableColumns` / `hiddenColumnIds`)
 * przez `onSettingsChange`.
 *
 * Kolumny rejestru są stałe (bez pól dodatkowych) i nie zależą od danych tenanta, więc edytor jest
 * zapisywalny zawsze, gdy kreator poda `onSettingsChange` — także w kreatorze panelu ITE. Kolumna
 * „Akcje" (usuwanie wiersza) nie jest kolumną układu: w edycji zawsze zostaje na końcu tabeli.
 *
 * Układ systemowy (`systemHideOnly`) przełącza edytor w tryb `hideOnly`: sam panel „Kolumny" z
 * checkboxami, a akcja wysyła wyłącznie `{ hiddenColumnIds }` (kreator wyprowadza z tego nakładkę
 * tenanta kluczowaną typem elementu).
 *
 * Korzeń (`className`/`style`): `LayoutElementRegistry.applyElementStyle` klonuje zwrócony element
 * i dokłada styl elementu, więc propsy trafiają na korzeń DOM edytora.
 */

import { type CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableLayoutEditor } from '@/components/embedded-table/TableLayoutEditor';
import type { LayoutElement } from '@/components/fields/LayoutElementRegistry';
import { vatRegisterDescriptors } from '@/lib/embedded-table/vat-register-descriptors';
import { cn } from '@/lib/utils';

interface Props {
  element: LayoutElement;
  /** Zapis łatki `settings` elementu; brak = podgląd tylko do odczytu. */
  onSettingsChange?: (patch: Record<string, unknown>) => void;
  /**
   * Układ systemowy — edytor w trybie `hideOnly` (tenant tylko ukrywa kolumny; łatka
   * `{ hiddenColumnIds }`). Bez `onSettingsChange` i tak zostaje podglądem bez kontrolek.
   */
  systemHideOnly?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function VatRegisterColumnsCanvas({
  element,
  onSettingsChange,
  systemHideOnly = false,
  className,
  style,
}: Props) {
  const { t } = useTranslation();
  const descriptors = useMemo(() => vatRegisterDescriptors(t), [t]);

  return (
    <TableLayoutEditor
      descriptors={descriptors}
      settings={element.settings}
      onChange={onSettingsChange}
      mode={systemHideOnly ? 'hideOnly' : 'full'}
      title={t('tenant.documents.vatRegister.paletteLabel')}
      // `min-w-0` — szeroka tabela przewija się w karcie (pasek nagłówków ma `overflow-x-auto`),
      // zamiast rozpychać rodzica.
      className={cn('min-w-0', className)}
      style={style}
      testIdPrefix={`vat-register-view-element-${element.id}`}
    />
  );
}
